import { useMemo } from 'react';
import {
	duplicateFlowDateSlots,
	flowDateSlots,
	headerRowFlowDateSlots,
} from '@dealstack/shared/lib/common/business/documentFlow';
import type { DocumentFlowDoc } from '@dealstack/shared/lib/common/business/documentFlow';
import { useSignatureSlotsQuery } from '../../api/useSignatureSlotsQuery.ts';

interface Result {
	requiresPostDating: boolean;
	duplicatedLabels: string[];
	headerRowLabels: string[];
	/** True when the document's date anchors are in a state the save will accept. */
	isComplete: boolean;
}

/** 1-based, to match the server's messages. */
const dateLabel = (index: number): string => `Date #${index + 1}`;

/**
 * Save checks for a Word document's date anchors, and the template's `requiresPostDating` flag.
 * The issued-template lock is server-only; its refusal surfaces as `saveFailureReason`.
 */
export const useDocxDateAnchors = (
	templateId: string,
	document: DocumentFlowDoc | null
): Result => {
	const { data } = useSignatureSlotsQuery(templateId);
	const requiresPostDating = data?.requiresPostDating ?? false;

	const duplicatedLabels = useMemo(() => {
		if (document === null) {
			return [];
		}
		const order = flowDateSlots(document).map((slot) => slot.templateDatePlacementId);
		return duplicateFlowDateSlots(document).map((slot) =>
			dateLabel(order.indexOf(slot.templateDatePlacementId))
		);
	}, [document]);

	const headerRowLabels = useMemo(() => {
		if (document === null) {
			return [];
		}
		const order = flowDateSlots(document).map((slot) => slot.templateDatePlacementId);
		return headerRowFlowDateSlots(document).map((slot) =>
			dateLabel(order.indexOf(slot.templateDatePlacementId))
		);
	}, [document]);

	return {
		requiresPostDating,
		duplicatedLabels,
		headerRowLabels,
		isComplete: duplicatedLabels.length === 0 && headerRowLabels.length === 0,
	};
};
